import React, { useState, useEffect, useCallback } from 'react';
import { 
  BookOpen, Plus, Edit2, Trash2, RefreshCw, Save, Eye, 
  Image, Sparkles, Send, ChevronDown, ChevronUp, 
  FileText, Calendar, User, Quote, Newspaper
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import * as api from '../../services/api';

const EditionsManager = () => {
  const [editions, setEditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingEdition, setEditingEdition] = useState(null);
  const [processing, setProcessing] = useState(null);

  const fetchEditions = useCallback(async () => {
    try {
      const data = await api.getEditions();
      setEditions(data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEditions();
  }, [fetchEditions]);

  const handleDelete = async (editionId) => {
    if (!window.confirm('¿Eliminar esta edición?')) return;
    setProcessing(editionId);
    try {
      await api.deleteEdition(editionId);
      await fetchEditions();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(null);
    }
  };

  const handlePublish = async (editionId) => {
    setProcessing(editionId + '_pub');
    try {
      await api.publishEdition(editionId);
      await fetchEditions();
      alert('Edición publicada');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(null);
    }
  };

  const handleUnpublish = async (editionId) => {
    setProcessing(editionId + '_pub');
    try {
      await api.unpublishEdition(editionId);
      await fetchEditions();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Ediciones Semanales
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {editions.length} ediciones - Crear y gestionar ediciones editoriales
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchEditions} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nueva Edición
          </Button>
        </div>
      </div>

      {editions.length === 0 ? (
        <div className="bg-white rounded-xl border p-8 text-center">
          <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No hay ediciones</p>
          <Button onClick={() => setShowCreateModal(true)} className="mt-4">
            <Plus className="w-4 h-4 mr-2" />
            Crear Primera Edición
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {editions.map((edition) => (
            <EditionCard
              key={edition.edition_id}
              edition={edition}
              onEdit={() => setEditingEdition(edition)}
              onDelete={() => handleDelete(edition.edition_id)}
              onPublish={() => handlePublish(edition.edition_id)}
              onUnpublish={() => handleUnpublish(edition.edition_id)}
              processing={processing}
            />
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <EditionEditModal
          onClose={() => setShowCreateModal(false)}
          onSave={async (data) => {
            await api.createEdition(data);
            await fetchEditions();
            setShowCreateModal(false);
          }}
        />
      )}

      {/* Edit Modal */}
      {editingEdition && (
        <EditionEditModal
          edition={editingEdition}
          onClose={() => setEditingEdition(null)}
          onSave={async (data) => {
            await api.updateEdition(editingEdition.edition_id, data);
            await fetchEditions();
            setEditingEdition(null);
          }}
        />
      )}
    </div>
  );
};

// ======================= EDITION CARD =======================
const EditionCard = ({ edition, onEdit, onDelete, onPublish, onUnpublish, processing }) => {
  const coverImage = edition.cover_image || edition.cover_data?.coverImage;
  
  return (
    <div className="bg-white rounded-xl border overflow-hidden">
      {/* Cover Preview */}
      <div className="relative aspect-[3/4] bg-gray-900">
        {coverImage ? (
          <img src={coverImage} alt="" className="w-full h-full object-cover opacity-80" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <BookOpen className="w-12 h-12 text-gray-600" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        
        {/* Status Badge */}
        <div className="absolute top-2 right-2">
          <Badge className={edition.status === 'published' ? 'bg-green-500' : 'bg-yellow-500'}>
            {edition.status === 'published' ? 'Publicada' : 'Borrador'}
          </Badge>
        </div>
        
        {/* Edition Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
          <p className="text-xs text-white/60 mb-1">Semana {edition.week_number}, {edition.year}</p>
          <h3 className="font-bold text-lg line-clamp-2">{edition.title}</h3>
          {edition.cover_data?.personName && (
            <p className="text-sm text-white/80 mt-1">{edition.cover_data.personName}</p>
          )}
        </div>
      </div>
      
      {/* Actions */}
      <div className="p-3 flex items-center justify-between bg-gray-50">
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={onEdit}>
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" className="text-red-500" onClick={onDelete}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
        {edition.status === 'published' ? (
          <Button 
            size="sm" 
            variant="outline"
            onClick={onUnpublish}
            disabled={processing === edition.edition_id + '_pub'}
          >
            Despublicar
          </Button>
        ) : (
          <Button 
            size="sm"
            onClick={onPublish}
            disabled={processing === edition.edition_id + '_pub'}
          >
            {processing === edition.edition_id + '_pub' ? (
              <RefreshCw className="w-4 h-4 animate-spin mr-1" />
            ) : null}
            Publicar
          </Button>
        )}
      </div>
    </div>
  );
};

// ======================= EDITION EDIT MODAL =======================
const EditionEditModal = ({ edition, onClose, onSave }) => {
  const currentWeek = Math.ceil((new Date() - new Date(new Date().getFullYear(), 0, 1)) / (7 * 24 * 60 * 60 * 1000));
  const currentYear = new Date().getFullYear();
  
  const [form, setForm] = useState({
    week_number: edition?.week_number || currentWeek,
    year: edition?.year || currentYear,
    title: edition?.title || '',
    summary: edition?.summary || '',
    cover_image: edition?.cover_image || '',
    cover_data: edition?.cover_data || {
      coverlines: [],
      mainHeadline: '',
      personName: '',
      personDescription: '',
      quote: '',
    },
    sections: edition?.sections || [],
    featured_dossiers: edition?.featured_dossiers || [],
  });
  
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [suggestingField, setSuggestingField] = useState(null);
  const [aiInstruction, setAiInstruction] = useState('');
  const [showAiInput, setShowAiInput] = useState(null);

  const handleSubmit = async () => {
    if (!form.title) {
      alert('El título es requerido');
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleAiSuggest = async (field) => {
    setSuggestingField(field);
    try {
      const context = {
        title: form.title,
        personName: form.cover_data?.personName,
        week: form.week_number,
        year: form.year,
      };
      
      const res = await api.suggestEditionContent({
        field,
        instruction: aiInstruction,
        context,
      });
      
      if (res.success && res.suggestion) {
        if (field === 'coverlines') {
          const lines = res.suggestion.split('\n').filter(l => l.trim());
          setForm(prev => ({
            ...prev,
            cover_data: { ...prev.cover_data, coverlines: lines }
          }));
        } else if (['mainHeadline', 'personDescription', 'quote'].includes(field)) {
          setForm(prev => ({
            ...prev,
            cover_data: { ...prev.cover_data, [field]: res.suggestion }
          }));
        } else if (field === 'summary') {
          setForm(prev => ({ ...prev, summary: res.suggestion }));
        }
        setShowAiInput(null);
        setAiInstruction('');
      }
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSuggestingField(null);
    }
  };

  const updateCoverData = (key, value) => {
    setForm(prev => ({
      ...prev,
      cover_data: { ...prev.cover_data, [key]: value }
    }));
  };

  const addSection = () => {
    setForm(prev => ({
      ...prev,
      sections: [...prev.sections, {
        section_type: 'cover_story',
        title: '',
        content: '',
        author: '',
      }]
    }));
  };

  const updateSection = (index, key, value) => {
    const sections = [...form.sections];
    sections[index] = { ...sections[index], [key]: value };
    setForm(prev => ({ ...prev, sections }));
  };

  const removeSection = (index) => {
    setForm(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }));
  };

  const AiSuggestButton = ({ field, label }) => (
    <div className="relative">
      {showAiInput === field ? (
        <div className="flex gap-2 items-center">
          <Input
            value={aiInstruction}
            onChange={(e) => setAiInstruction(e.target.value)}
            placeholder="Instrucción para la IA (opcional)"
            className="text-sm flex-1"
            onKeyPress={(e) => e.key === 'Enter' && handleAiSuggest(field)}
          />
          <Button
            size="sm"
            onClick={() => handleAiSuggest(field)}
            disabled={suggestingField === field}
          >
            {suggestingField === field ? (
              <RefreshCw className="w-3 h-3 animate-spin" />
            ) : (
              <Send className="w-3 h-3" />
            )}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowAiInput(null)}>
            ×
          </Button>
        </div>
      ) : (
        <Button
          size="sm"
          variant="ghost"
          className="text-purple-600 hover:text-purple-700 h-7 px-2"
          onClick={() => setShowAiInput(field)}
        >
          <Sparkles className="w-3 h-3 mr-1" />
          Sugerir
        </Button>
      )}
    </div>
  );

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            {edition ? 'Editar Edición' : 'Nueva Edición'}
          </DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-2 border-b pb-2">
          {[
            { id: 'general', label: 'General', icon: FileText },
            { id: 'cover', label: 'Portada', icon: Image },
            { id: 'sections', label: 'Secciones', icon: Newspaper },
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon className="w-4 h-4 mr-1" />
              {tab.label}
            </Button>
          ))}
        </div>

        <div className="space-y-6 py-4">
          {/* General Tab */}
          {activeTab === 'general' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Semana</label>
                  <Input
                    type="number"
                    value={form.week_number}
                    onChange={(e) => setForm({ ...form, week_number: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Año</label>
                  <Input
                    type="number"
                    value={form.year}
                    onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) })}
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">Título de la Edición</label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Ej: Latinoamérica en Perspectiva"
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium">Resumen</label>
                  <AiSuggestButton field="summary" />
                </div>
                <Textarea
                  value={form.summary}
                  onChange={(e) => setForm({ ...form, summary: e.target.value })}
                  placeholder="Breve descripción de esta edición..."
                  rows={3}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">URL de Imagen de Portada</label>
                <Input
                  value={form.cover_image}
                  onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                  placeholder="https://..."
                />
                {form.cover_image && (
                  <img src={form.cover_image} alt="" className="mt-2 h-32 object-cover rounded" />
                )}
              </div>
            </div>
          )}

          {/* Cover Tab */}
          {activeTab === 'cover' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                Configura los elementos de la portada editorial estilo Forbes
              </p>
              
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Nombre de la Persona
                  </label>
                </div>
                <Input
                  value={form.cover_data?.personName || ''}
                  onChange={(e) => updateCoverData('personName', e.target.value)}
                  placeholder="Ej: Diego Urquijo"
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium">Descripción de la Persona</label>
                  <AiSuggestButton field="personDescription" />
                </div>
                <Input
                  value={form.cover_data?.personDescription || ''}
                  onChange={(e) => updateCoverData('personDescription', e.target.value)}
                  placeholder="Ej: Arquitecto de sistemas empresariales"
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Quote className="w-4 h-4" />
                    Cita
                  </label>
                  <AiSuggestButton field="quote" />
                </div>
                <Textarea
                  value={form.cover_data?.quote || ''}
                  onChange={(e) => updateCoverData('quote', e.target.value)}
                  placeholder='"Las ideas inspiran. Los sistemas gobiernan."'
                  rows={2}
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium">Titular Principal (Headline)</label>
                  <AiSuggestButton field="mainHeadline" />
                </div>
                <Input
                  value={form.cover_data?.mainHeadline || ''}
                  onChange={(e) => updateCoverData('mainHeadline', e.target.value)}
                  placeholder="EL PODER DE DISEÑAR SISTEMAS"
                  className="uppercase"
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium">Coverlines (frases de portada)</label>
                  <AiSuggestButton field="coverlines" />
                </div>
                <Textarea
                  value={(form.cover_data?.coverlines || []).join('\n')}
                  onChange={(e) => updateCoverData('coverlines', e.target.value.split('\n').filter(l => l.trim()))}
                  placeholder="Una línea por frase..."
                  rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">Una frase por línea</p>
              </div>
            </div>
          )}

          {/* Sections Tab */}
          {activeTab === 'sections' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Secciones de contenido de la edición
                </p>
                <Button size="sm" onClick={addSection}>
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar Sección
                </Button>
              </div>
              
              {form.sections.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Newspaper className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No hay secciones</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {form.sections.map((section, index) => (
                    <SectionEditor
                      key={index}
                      section={section}
                      index={index}
                      onUpdate={(key, value) => updateSection(index, key, value)}
                      onRemove={() => removeSection(index)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ======================= SECTION EDITOR =======================
const SectionEditor = ({ section, index, onUpdate, onRemove }) => {
  const [expanded, setExpanded] = useState(true);
  const [suggestingField, setSuggestingField] = useState(null);
  
  const sectionTypes = [
    { value: 'cover_story', label: 'Historia de Portada' },
    { value: 'entrevista_exclusiva', label: 'Entrevista Exclusiva' },
    { value: 'análisis', label: 'Análisis' },
    { value: 'impacto_latam', label: 'Impacto LATAM' },
    { value: 'opinion', label: 'Opinión' },
    { value: 'tendencias', label: 'Tendencias' },
  ];

  const handleAiSuggest = async (field) => {
    setSuggestingField(field);
    try {
      const res = await api.suggestEditionContent({
        field: field === 'title' ? 'section_title' : 'section_content',
        instruction: '',
        context: {
          section_type: section.section_type,
          current_title: section.title,
        },
      });
      
      if (res.success && res.suggestion) {
        onUpdate(field, res.suggestion);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setSuggestingField(null);
    }
  };
  
  return (
    <div className="border rounded-lg overflow-hidden">
      <div 
        className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          <span className="font-medium text-sm">
            {section.title || `Sección ${index + 1}`}
          </span>
          <Badge variant="outline" className="text-xs">
            {sectionTypes.find(t => t.value === section.section_type)?.label || section.section_type}
          </Badge>
        </div>
        <Button 
          size="sm" 
          variant="ghost" 
          className="text-red-500"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      
      {expanded && (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Tipo de Sección</label>
              <select
                value={section.section_type}
                onChange={(e) => onUpdate('section_type', e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
              >
                {sectionTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Autor</label>
              <Input
                value={section.author || ''}
                onChange={(e) => onUpdate('author', e.target.value)}
                placeholder="Nombre del autor"
              />
            </div>
          </div>
          
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium">Título</label>
              <Button
                size="sm"
                variant="ghost"
                className="text-purple-600 h-6 px-2"
                onClick={() => handleAiSuggest('title')}
                disabled={suggestingField === 'title'}
              >
                {suggestingField === 'title' ? (
                  <RefreshCw className="w-3 h-3 animate-spin mr-1" />
                ) : (
                  <Sparkles className="w-3 h-3 mr-1" />
                )}
                Sugerir
              </Button>
            </div>
            <Input
              value={section.title || ''}
              onChange={(e) => onUpdate('title', e.target.value)}
              placeholder="Título de la sección"
            />
          </div>
          
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium">Contenido</label>
              <Button
                size="sm"
                variant="ghost"
                className="text-purple-600 h-6 px-2"
                onClick={() => handleAiSuggest('content')}
                disabled={suggestingField === 'content'}
              >
                {suggestingField === 'content' ? (
                  <RefreshCw className="w-3 h-3 animate-spin mr-1" />
                ) : (
                  <Sparkles className="w-3 h-3 mr-1" />
                )}
                Sugerir
              </Button>
            </div>
            <Textarea
              value={section.content || ''}
              onChange={(e) => onUpdate('content', e.target.value)}
              placeholder="Contenido de la sección..."
              rows={5}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EditionsManager;
